import { trackEvent } from './tracking'

/**
 * Ensure a Vimeo URL has the `api=1` parameter required for postMessage events.
 */
export function ensureVimeoApi(url: string): string {
	if (!url.includes('vimeo.com')) return url
	if (url.includes('api=1')) return url
	return url + (url.includes('?') ? '&' : '?') + 'api=1'
}

// Tracks which milestone percentages have already fired per video URL
const firedMilestones = new Map<string, Set<number>>()
const MILESTONES = [25, 50, 75, 100]

function findVimeoIframe(source: MessageEventSource | null): { url: string; title: string | undefined } | null {
	if (!source) return null
	const iframes = document.querySelectorAll<HTMLIFrameElement>(
		'iframe[src*="vimeo.com"]',
	)
	for (const iframe of iframes) {
		if (iframe.contentWindow === source) {
			return {
				url: iframe.src,
				title: iframe.title || undefined,
			}
		}
	}
	return null
}

const SUBSCRIBE_EVENTS = ['play', 'pause', 'finish', 'playProgress']

function subscribeToVimeoEvents(iframe: HTMLIFrameElement) {
	for (const eventName of SUBSCRIBE_EVENTS) {
		iframe.contentWindow?.postMessage(
			JSON.stringify({ method: 'addEventListener', value: eventName }),
			'*',
		)
	}
}

function handleVimeoMessage(event: MessageEvent) {
	if (!event.origin.includes('vimeo.com')) return

	let data: any
	try {
		data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
	} catch {
		return
	}

	if (!data || !data.event) return

	// When a Vimeo player is ready, subscribe to the events we care about
	if (data.event === 'ready') {
		const iframes = document.querySelectorAll<HTMLIFrameElement>(
			'iframe[src*="vimeo.com"]',
		)
		for (const iframe of iframes) {
			if (iframe.contentWindow === event.source) {
				subscribeToVimeoEvents(iframe)
				break
			}
		}
		return
	}

	const iframe = findVimeoIframe(event.source)
	if (!iframe) return

	const videoUrl = iframe.url
	const videoTitle = iframe.title

	switch (data.event) {
		case 'play':
			trackEvent('video', {
				video_action: 'play',
				video_url: videoUrl,
				video_title: videoTitle,
			})
			break

		case 'pause':
			trackEvent('video', {
				video_action: 'pause',
				video_url: videoUrl,
				video_title: videoTitle,
			})
			break

		case 'finish':
			trackEvent('video', {
				video_action: 'ended',
				video_url: videoUrl,
				video_percent: 100,
				video_title: videoTitle,
			})
			break

		case 'playProgress': {
			const percent = data.data?.percent
			if (typeof percent !== 'number') break
			const pct = Math.floor(percent * 100)

			if (!firedMilestones.has(videoUrl)) {
				firedMilestones.set(videoUrl, new Set())
			}
			const fired = firedMilestones.get(videoUrl)!

			for (const milestone of MILESTONES) {
				if (pct >= milestone && !fired.has(milestone)) {
					fired.add(milestone)
					trackEvent('video', {
						video_action: 'progress',
						video_url: videoUrl,
						video_percent: milestone,
						video_title: videoTitle,
					})
				}
			}
			break
		}
	}
}

let listening = false

export function initVimeoTracking() {
	if (typeof window === 'undefined' || listening) return
	window.addEventListener('message', handleVimeoMessage)
	listening = true
}

export function cleanupVimeoTracking() {
	if (typeof window === 'undefined' || !listening) return
	window.removeEventListener('message', handleVimeoMessage)
	firedMilestones.clear()
	listening = false
}
